const RegexFormatMoney = /\B(?=(\d{3})+(?!\d))/g

/**
 * Esta expresión puede utilizarse para identificar casos donde una imprecisión de coma flotante
 * genera una secuencia decimal interminable o artificialmente larga.
 *
 */
const RegexHasFloatingPoint = /\.(\d*?)(\d)\2{8,}\d*$/

const truncateNumber = (num, precision) => {
  const factor = Math.pow(10, precision)
  return Math.trunc(num * factor) / factor
}

const applyFormatMoney = (integerPart, decimalPart) => {
  const formattedInteger = integerPart.toString().replace(RegexFormatMoney, ',')

  if (decimalPart) {
    const formattedDecimal = `${formattedInteger}.${decimalPart}`
    return formattedDecimal
  }

  return formattedInteger
}

/**
 * Normaliza un número decimal redondeándolo a 4 decimales de forma segura.
 *
 * Si el valor proporcionado no contiene parte decimal (según el patrón `RegexHasFloatingPoint`),
 * se devuelve tal cual. En caso contrario, se aplica redondeo seguro usando `Number.EPSILON`
 * para evitar errores por coma flotante.
 *
 * @depends {RegexHasFloatingPoint} RegexHasFloatingPoint
 * Expresión regular que detecta la presencia de una parte decimal con un patrón repetitivo,
 * específicamente cuando un mismo dígito se repite más de 8 veces consecutivas en los decimales.
 *
 * @param {number} value - El número que se desea normalizar.
 * @returns {number} El número redondeado a 4 decimales.
 */
const normalizeDecimal = value => {
  if (!RegexHasFloatingPoint.test(value.toString())) {
    return value
  }

  return Math.round((value + Number.EPSILON) * 10000000000000) / 10000000000000
}

/**
 * Formatea un número en formato monetario, aplicando redondeo o truncamiento a 4 decimales
 * y asegurando que la salida tenga entre 2 y 4 dígitos decimales.
 *
 * Opciones:
 * - rounded: Si es true, se aplicará redondeo; de lo contrario se truncará.
 * - roundingMethod: Método de redondeo a utilizar cuando rounded es true.
 *    - 'default': Redondeo convencional (Math.round).
 *    - 'ceil': Redondeo hacia arriba (Math.ceil).
 * - ignoreDecimal: Si es true, se retorna únicamente la parte entera sin formatear la parte decimal.
 * - completeWithZero: Si es true, se completará la parte decimal con ceros hasta 2 dígitos.
 * - withFormat: Si es true, se aplicará el formato monetario (con comas y punto decimal).
 *
 * @param {number|string} number
 * @param {Object} [options] - Opciones de formateo.
 * @param {boolean} [options.rounded=false]
 * @param {boolean} [options.decimalPrecision=4]
 * @param {'default'|'ceil'} [options.roundingMethod='default']
 * @param {boolean} [options.ignoreDecimal=false]
 * @param {boolean} [options.completeWithZero=false]
 * @param {boolean} [options.withFormat=true]
 *
 * @returns {string|number} El número formateado como cadena (o número entero si se ignora la parte decimal).
 */
const MoneyFormatter = (number, options = {}) => {
  const {
    rounded = false,
    decimalPrecision = 4,
    roundingMethod = 'default', // 'default' (Math.round) o 'ceil' (Math.ceil)
    ignoreDecimal = false,
    completeWithZero = false,
    withFormat = true,
  } = options

  if (number === null || number === undefined || isNaN(Number(number))) {
    return '0.00'
  }

  const parseNumber = Number(number)

  if (ignoreDecimal) {
    return Math.trunc(parseNumber).toString()
  }

  // FACTOR = 10^n, determinado por decimalPrecision (n entre 2 y 4).
  const FACTOR_MULTIPLIER = Math.pow(10, decimalPrecision)
  let adjustedNumber = parseNumber

  const originalDecimal = adjustedNumber.toString().split('.')[1]

  if (originalDecimal && originalDecimal.length > decimalPrecision) {
    if (rounded) {
      const roundingFunction =
        roundingMethod === 'ceil' ? Math.ceil : Math.round
      adjustedNumber =
        roundingFunction(parseNumber * FACTOR_MULTIPLIER) / FACTOR_MULTIPLIER
    } else {
      adjustedNumber = truncateNumber(
        normalizeDecimal(parseNumber),
        decimalPrecision
      )
    }
  } else {
    adjustedNumber = normalizeDecimal(parseNumber).toFixed(decimalPrecision)
  }

  const [integerPart, rawDecimal = ''] = adjustedNumber.toString().split('.')
  let decimalPart = rawDecimal.padEnd(decimalPrecision, '0')

  // Se eliminan los ceros a la derecha de la parte decimal,
  // pero se mantiene al menos 2 dígitos decimales.
  if (!completeWithZero && decimalPart) {
    while (decimalPart.length > 2 && decimalPart.endsWith('0')) {
      decimalPart = decimalPart.slice(0, -1)
    }
  }

  if (withFormat) {
    return applyFormatMoney(integerPart, decimalPart)
  }

  return adjustedNumber
}

export const Formatter = {
  Money: MoneyFormatter,
}
